import { useTranslation } from '@eti/providers';
import { price as priceUtil } from '@eti/utils';
import uniqBy from 'lodash/uniqBy';
import type { TransformedPaymentMethod } from '../models';
import { paymentMethodCodes, paymentMethodTypes } from '../constants/paymentMethods';

const { BANK, CARD, WALLET } = paymentMethodTypes;
const {
  APP_CARD,
  UPI,
  KLARNA_ONE,
  KLARNA_PAY_LATER,
  KLARNA_PAY_NOW,
  KLARNA_SLICE_IT,
  KLARNA_SOFORT,
} = paymentMethodCodes;
const getUniquePrices = (prices: number[]) => uniqBy(prices, (value) => value ?? 0);

const getAllUniqueFeesOrDiscounts = (paymentMethod: TransformedPaymentMethod): number[] => {
  if (
    paymentMethod.type === WALLET &&
    (paymentMethod.code === UPI || paymentMethod.code === APP_CARD)
  ) {
    const optionPrices = paymentMethod.options.map((option) => option.price.value);
    return getUniquePrices(optionPrices);
  }

  if (paymentMethod.type === BANK || paymentMethod.type === WALLET) {
    return [paymentMethod.price.value];
  }

  if (paymentMethod.type === CARD) {
    const cardPrices = paymentMethod.creditCards.map((card) => card.price.value);
    return getUniquePrices(cardPrices);
  }

  return [];
};

const klarnaPaymentMethodCodes: TransformedPaymentMethod['code'][] = [
  KLARNA_ONE,
  KLARNA_PAY_LATER,
  KLARNA_PAY_NOW,
  KLARNA_SLICE_IT,
  KLARNA_SOFORT,
];

const usePaymentMethodFees = (paymentMethod: TransformedPaymentMethod) => {
  const { t } = useTranslation();
  const feesOrDiscounts = getAllUniqueFeesOrDiscounts(paymentMethod);
  const lowestPrice = Math.min(...feesOrDiscounts);

  if (klarnaPaymentMethodCodes.includes(paymentMethod.code)) {
    return {
      isDiscount: false,
      label: t('Payment.PaymentMethodFeeMayApply.Label'),
    };
  }

  if (paymentMethod.code === APP_CARD) {
    return {
      isDiscount: false,
      label: null,
    };
  }

  if (feesOrDiscounts.length === 0 || (feesOrDiscounts.length === 1 && lowestPrice === 0)) {
    return {
      isDiscount: false,
      label: t('Payment.PaymentMethodNoFee.Label'),
    };
  }

  const isDiscount = lowestPrice < 0;
  const formattedPrice = priceUtil.formatPrice(lowestPrice);

  if (feesOrDiscounts.length > 1) {
    return {
      isDiscount,
      label: t(
        isDiscount
          ? 'Payment.PaymentMethodStartingFromDiscountFee.Label'
          : 'General.Price.StartingFrom',
        formattedPrice,
      ),
    };
  }

  return {
    isDiscount,
    label: isDiscount
      ? t('Payment.PaymentMethodDiscountFee.Label', formattedPrice)
      : formattedPrice,
  };
};

export default usePaymentMethodFees;
